import { PageLayout } from '../components/Layout';
import { Link } from 'react-router-dom';
import { Heart, ArrowLeft, FileText, ShieldCheck, Handshake, Warning, Scales } from '@phosphor-icons/react';

const TermsOfService = () => {
  return (
    <PageLayout>
      <div className="py-12 md:py-20">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-6">
              <FileText weight="light" className="w-8 h-8 text-primary" />
            </div>
            <h1 className="font-serif text-4xl md:text-5xl mb-4">Terms of Service</h1>
            <p className="text-muted-foreground">
              Our agreement with you
            </p>
            <p className="text-sm text-muted-foreground mt-2">Last Updated: February 2026</p>
          </div>

          {/* Content */}
          <div className="paper-card rounded-lg p-8 md:p-12 space-y-8">
            <section>
              <h2 className="font-serif text-2xl mb-4 flex items-center gap-3">
                <Heart weight="fill" className="w-6 h-6 text-primary" />
                Welcome to Link, Laugh & Love
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                By using our matchmaking services, you agree to these Terms of Service. We've tried to make them clear 
                and fair - because a good relationship (even a business one!) starts with mutual understanding.
              </p>
            </section>

            <section>
              <h2 className="font-serif text-2xl mb-4">1. Eligibility</h2>
              <div className="space-y-3 text-muted-foreground">
                <p>To use Link, Laugh & Love, you must:</p>
                <ul className="space-y-2 ml-4">
                  <li className="flex items-start gap-2">
                    <span className="text-primary">•</span>
                    <span>Be at least 21 years old</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary">•</span>
                    <span>Be legally single (unmarried, divorced, or widowed)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary">•</span>
                    <span>Be genuinely seeking a committed relationship</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary">•</span>
                    <span>Provide truthful and accurate information</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary">•</span>
                    <span>Have the legal capacity to enter into agreements</span>
                  </li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="font-serif text-2xl mb-4">2. Our Services</h2>
              <p className="text-muted-foreground mb-4">
                Link, Laugh & Love provides personalized matchmaking services, including:
              </p>
              <ul className="space-y-2 text-muted-foreground ml-4">
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  <span>Personal consultation to understand your relationship goals</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  <span>Hand-selected matches based on compatibility</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  <span>Coordinated video introductions</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  <span>Ongoing feedback and match refinement</span>
                </li>
              </ul>
              <div className="bg-pink-50 rounded-lg p-4 mt-4">
                <p className="text-sm text-muted-foreground">
                  <strong>Important:</strong> We are a matchmaking service, not a dating app. We facilitate introductions 
                  between compatible individuals, but we cannot guarantee any specific outcome, including relationships, 
                  marriage, or compatibility.
                </p>
              </div>
            </section>

            <section>
              <h2 className="font-serif text-2xl mb-4 flex items-center gap-3">
                <Handshake weight="fill" className="w-6 h-6 text-primary" />
                3. Your Responsibilities
              </h2>
              <div className="space-y-4 text-muted-foreground">
                <p><strong>You agree to:</strong></p>
                <ul className="space-y-2 ml-4">
                  <li className="flex items-start gap-2">
                    <span className="text-green-500">✓</span>
                    <span>Provide accurate, current, and complete information</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-500">✓</span>
                    <span>Treat all matches and our staff with respect</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-500">✓</span>
                    <span>Keep your account credentials secure</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-500">✓</span>
                    <span>Respond to communications in a timely manner</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-500">✓</span>
                    <span>Report any concerns or inappropriate behavior</span>
                  </li>
                </ul>

                <p className="mt-4"><strong>You agree NOT to:</strong></p>
                <ul className="space-y-2 ml-4">
                  <li className="flex items-start gap-2">
                    <span className="text-red-400">✗</span>
                    <span>Misrepresent yourself or provide false information</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-400">✗</span>
                    <span>Harass, threaten, or behave inappropriately toward matches</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-400">✗</span>
                    <span>Use the service for any commercial or non-romantic purposes</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-400">✗</span>
                    <span>Share confidential information about your matches</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-400">✗</span>
                    <span>Attempt to contact matches outside our coordinated introductions without mutual consent</span>
                  </li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="font-serif text-2xl mb-4">4. Fees and Payment</h2>
              <div className="space-y-3 text-muted-foreground">
                <p>Our services require payment as outlined in our pricing plans:</p>
                <ul className="space-y-2 ml-4">
                  <li>• <strong>Link, Laugh & Love Package:</strong> Personalized matchmaking experience</li>
                  <li>• <strong>Additional Services:</strong> Polished Personal Résumé, Extra Consultation, Date Planning</li>
                </ul>
                <p className="mt-4">
                  Payment is due before matches are introduced. See our <Link to="/refund-policy" className="text-primary hover:underline">Refund Policy</Link> for 
                  details on cancellations and refunds.
                </p>
              </div>
            </section>

            <section>
              <h2 className="font-serif text-2xl mb-4 flex items-center gap-3">
                <ShieldCheck weight="fill" className="w-6 h-6 text-primary" />
                5. Privacy and Confidentiality
              </h2>
              <p className="text-muted-foreground mb-4">
                Your privacy is important to us. We collect and use your information only for matchmaking purposes. 
                We will never share your personal details with a match without your explicit consent.
              </p>
              <p className="text-muted-foreground">
                For full details, please read our <Link to="/anti-stalking-policy" className="text-primary hover:underline">Privacy & Anti-Stalking Policy</Link>.
              </p>
            </section>

            <section>
              <h2 className="font-serif text-2xl mb-4 flex items-center gap-3">
                <Warning weight="fill" className="w-6 h-6 text-amber-500" />
                6. Limitation of Liability
              </h2>
              <div className="bg-amber-50 rounded-lg p-6 text-sm text-muted-foreground space-y-3">
                <p>
                  Link, Laugh & Love provides matchmaking introductions in good faith, but we cannot be held responsible for:
                </p>
                <ul className="space-y-2 ml-4">
                  <li>• The actions, behavior, or honesty of any match</li>
                  <li>• The outcome of any introduction or relationship</li>
                  <li>• Any emotional, financial, or personal consequences</li>
                  <li>• Information provided by other users that turns out to be inaccurate</li>
                </ul>
                <p className="mt-4">
                  You agree to exercise your own judgment and take reasonable precautions when meeting anyone through our service.
                </p>
              </div>
            </section>

            <section>
              <h2 className="font-serif text-2xl mb-4">7. Termination</h2>
              <p className="text-muted-foreground mb-4">
                We reserve the right to suspend or terminate your account if you violate these terms or engage in behavior 
                that harms our community.
              </p>
            </section>

            <section>
              <h2 className="font-serif text-2xl mb-4 flex items-center gap-3">
                <Scales weight="fill" className="w-6 h-6 text-primary" />
                8. Dispute Resolution
              </h2>
              <p className="text-muted-foreground">
                If any dispute arises, we encourage you to contact us first at <a href="mailto:LinkLaughLoveMatch@gmail.com" className="text-primary hover:underline">LinkLaughLoveMatch@gmail.com</a>. 
                We'll work in good faith to resolve any issues. These terms are governed by the laws of the State of Michigan.
              </p>
            </section>

            <section>
              <h2 className="font-serif text-2xl mb-4">9. Changes to Terms</h2>
              <p className="text-muted-foreground">
                We may update these terms from time to time. We'll notify you of significant changes via email or through our platform. 
                Continued use of our services after changes means you accept the updated terms.
              </p>
            </section>

            <section className="bg-gradient-to-r from-primary/10 to-pink-50 rounded-lg p-6">
              <h2 className="font-serif text-xl mb-3">Questions About Our Terms?</h2>
              <p className="text-muted-foreground mb-4">
                We're happy to clarify anything. Reach out anytime!
              </p>
              <div className="flex flex-wrap gap-4 text-sm">
                <a href="mailto:LinkLaughLoveMatch@gmail.com" className="text-primary hover:underline">
                  LinkLaughLoveMatch@gmail.com
                </a>
                <a href="tel:8107991060" className="text-primary hover:underline">
                  (810) 799-1060
                </a>
              </div>
            </section>
          </div>

          <div className="text-center mt-8">
            <Link to="/" className="inline-flex items-center gap-2 text-primary hover:underline">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default TermsOfService;
